import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthShell } from "@/components/shared/AuthShell";
import { FileUpload } from "@/components/shared/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useMyProfileAvatarObjectUrl } from "@/hooks/useMyProfileAvatarObjectUrl";
import { updateProfile, uploadProfileImage } from "@/services/authService";

export function NameEntryPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, refreshUser } = useAuth();

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [firstError, setFirstError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const currentAvatarUrl = useMyProfileAvatarObjectUrl(
    user?.profileImage ?? null,
  );

  const handleSubmit = useCallback(async () => {
    const first = firstName.trim();
    const last = lastName.trim();
    setFirstError(first === "" ? t("auth.onboarding.name.firstRequired", "Please enter your first name.") : null);
    setLastError(last === "" ? t("auth.onboarding.name.lastRequired", "Please enter your last name.") : null);
    if (first === "" || last === "") return;

    setIsSaving(true);
    try {
      await updateProfile({ firstName: first, lastName: last });
      if (selectedFile != null) {
        await uploadProfileImage(selectedFile);
      }
      await refreshUser();
      navigate("/", { replace: true });
    } catch {
      toast.error(
        t("auth.onboarding.name.saveError", "Could not save. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  }, [firstName, lastName, selectedFile, refreshUser, navigate, t]);

  if (isLoading) return <></>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const hasName =
    (user?.firstName?.trim() ?? "") !== "" &&
    (user?.lastName?.trim() ?? "") !== "";
  if (hasName) return <Navigate to="/" replace />;

  return (
    /* Keeps the consumer cover: this screen is the second half of signup, so
       dropping the half here would make the funnel change shape mid-flow. No
       note — the neutral one offers a sign-up link they have just used. */
    <AuthShell
      cover={{
        title: t("auth.login.cover.userTitle"),
        body: t("auth.login.cover.userBody"),
      }}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("auth.onboarding.name.title", "Complete your profile")}
          </h1>
          <p className="text-balance text-muted-foreground">
            {t(
              "auth.onboarding.name.subtitle",
              "We need your name to personalize your account. You can also add a photo if you'd like.",
            )}
          </p>
        </div>

        <div className="space-y-2">
          <Label>{t("auth.onboarding.name.photoLabel", "Profile photo (optional)")}</Label>
          <FileUpload
            currentImageUrl={currentAvatarUrl}
            onFileChange={setSelectedFile}
            disabled={isSaving}
            showLabel={false}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="onboarding-firstName">
              {t("profile.firstName", "First name")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="onboarding-firstName"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (firstError != null) setFirstError(null);
              }}
              disabled={isSaving}
              autoFocus
            />
            {firstError != null && (
              <p className="text-xs text-destructive">{firstError}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="onboarding-lastName">
              {t("profile.lastName", "Last name")}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="onboarding-lastName"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                if (lastError != null) setLastError(null);
              }}
              disabled={isSaving}
            />
            {lastError != null && (
              <p className="text-xs text-destructive">{lastError}</p>
            )}
          </div>
        </div>

        <Button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={isSaving}
          className="w-full transition-colors duration-150"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              {t("auth.onboarding.name.saving", "Saving…")}
            </>
          ) : (
            t("auth.onboarding.name.continue", "Continue")
          )}
        </Button>
      </div>
    </AuthShell>
  );
}
